import classnames from 'classnames'
import { h, Fragment } from 'preact'
import { useEffect, useState } from 'preact/compat'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { checkPaymentStatus } from '../../../api/payments'
import { paymentStatuses } from '../../../constants/paymentStatuses'
import { useTranslations } from '../../../hooks/useTranslations'
import useFailureRedirect from '../../../hooks/useFailureRedirect'
import useAuthoriseError from '../../../hooks/useAuthoriseError'
import {
  formatAsCurrency,
  redirect,
  sprintf,
  getSupportPhoneNumber,
} from '../../../helpers/common'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import InfoMessage from '../../Common/InfoMessage'
import ErrorMessage from '../../Common/ErrorMessage'
import Loader from '../../Common/Loader'
import LinkButton from '../../Common/LinkButton'
import { handlePayment } from '../../../helpers/payment'
import { pageStateSelector } from '../../../redux/slice/pageStateSlice'
import { transactionDetailsSelector } from '../../../redux/slice/transactionDetailsSlice'
import './styles.scss'

const Vipps = ({
  handleSubmit,
  showPaymentConfirmation,
  isGoBackButtonVisible,
  loaderImage,
}) => {
  const l10n = useTranslations()
  const transactionDetails = useSelector(transactionDetailsSelector)
  const [error, setError] = useState('')
  const [isPaymentPending, setIsPaymentPending] = useState(false)
  const pageState = useSelector(pageStateSelector)
  const [isPaymentDisabled, setPaymentDisabled] = useState(true)
  const [isPaymentStatusAuthorised, setPaymentStatusAuthorised] =
    useState(false)
  const [isPaymentStatusPending, setIsPaymentStatusPending] = useState(true)
  const payButtonClasses = classnames('button', 'primary', 'medium', {
    loading: isPaymentStatusPending,
  })

  useEffect(() => {
    if (!pageState.isPaymentBlocked) {
      setError('')
    }
  }, [pageState.isPaymentBlocked])

  const handlePaymentStatus = () => {
    setIsPaymentStatusPending(true)
    checkPaymentStatus('vipps', transactionDetails.id).then(
      ({ responseData }) => {
        if (responseData?.status === paymentStatuses.AUTHORISE_REQUESTED) {
          setPaymentDisabled(true)
          setPaymentStatusAuthorised(true)
        } else {
          setPaymentDisabled(false)
          setPaymentStatusAuthorised(false)
        }

        setIsPaymentStatusPending(false)
      },
    )
  }

  useEffect(async () => {
    window.onpageshow = async event => {
      if (event.persisted) {
        await handlePaymentStatus()
      }
    }

    await handlePaymentStatus()
  }, [])

  useFailureRedirect()
  const handleAuthoriseError = useAuthoriseError()

  const formatAmount = formatAsCurrency(
    transactionDetails.currency,
    transactionDetails.culture,
  )

  const currentAmount = formatAmount(
    transactionDetails?.overrideAmount
      ? transactionDetails.overrideAmount
      : transactionDetails.amount,
  )

  const paymentData = {
    pageState,
    handleSubmit,
    transactionDetails,
    handleAuthoriseError,
    paymentMethod: PAYMENT_METHOD_TYPES.Vipps,
    setError,
    l10n,
    setIsPaymentPending,
  }

  return (
    <>
      <div className="vipps-wrapper">
        {showPaymentConfirmation && (
          <InfoMessage
            message={sprintf(
              l10n.t(`You're about to pay %s with Vipps`),
              currentAmount,
            )}
            data-testid="vipps-info-container"
            className="vipps-info-message"
          />
        )}
        <ErrorMessage
          isShown={!!error}
          message={error}
          data-testid="vipps-error"
        />

        <ErrorMessage
          isShown={isPaymentStatusAuthorised}
          message={l10n.t(
            'Your payment request is currently being processed, if you are not redirected, please contact customer services on',
          )}
        >
          <div className="support-phone-number">
            {getSupportPhoneNumber(transactionDetails.culture)}
          </div>
        </ErrorMessage>

        {isPaymentPending ? (
          <Loader loaderImage={loaderImage} />
        ) : (
          <div className="vipps-button">
            {isGoBackButtonVisible && (
              <LinkButton
                data-testid="go-back-button"
                title={l10n.t('Go Back')}
                onClick={() => redirect(transactionDetails.returnUrl)}
                className="go-back-button"
              />
            )}
            <button
              type="button"
              className={payButtonClasses}
              data-testid="vipps-button"
              onClick={() => handlePayment(paymentData)}
              disabled={isPaymentDisabled}
            >
              {`${l10n.t('Pay With')} ${l10n.t('Vipps')}`}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

Vipps.propTypes = {
  handleSubmit: PropTypes.func,
  showPaymentConfirmation: PropTypes.bool,
  isGoBackButtonVisible: PropTypes.bool,
  loaderImage: PropTypes.string,
}

export default Vipps
